import { useEffect, useRef, useState } from "react";
import Indicator from "../../../../components/base/Indicator";
import { useHub } from "../../../../core/Global";
import { WalletDateRange } from "./WalletDateFilter";


type ChartData = {
    start: string
    end: string
    bucket: string
    labels: string[]
    values: number[]
    total: number
}

const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
})


type Props = {
    range?: WalletDateRange
}

export default function WalletUsageChart({ range }: Props) {

    const hub = useHub()

    const [data, dataSet] = useState<ChartData>()
    const [fetching, fetchingSet] = useState<boolean>(false)

    const ref = useRef<HTMLDivElement>(null)
    const chartRef = useRef<any>(null)

    useEffect(() => {
        if (!range) return
        fetchingSet(true)
        hub.Get(`/accounts/profile/wallet/usage-chart?start=${range.start}&end=${range.end}`, res => {
            if (res?.success && res.data) dataSet(res.data)
            fetchingSet(false)
        })
    }, [range?.start, range?.end])

    useEffect(() => {
        if (!data || !ref.current) return
        const Apex = (window as any).ApexCharts
        const KTUtil = (window as any).KTUtil
        if (!Apex || !KTUtil) return

        const labelColor = KTUtil.getCssVariableValue("--bs-gray-500")
        const borderColor = KTUtil.getCssVariableValue("--bs-gray-200")
        const baseColor = KTUtil.getCssVariableValue("--bs-primary")

        const options = {
            series: [{ name: "Usage", data: data.values }],
            chart: {
                fontFamily: "inherit",
                type: "line",
                height: 300,
                toolbar: { show: false },
                // Drag-to-zoom/selection is disabled — it was confusing on this widget.
                zoom: { enabled: false },
                selection: { enabled: false }
            },
            legend: { show: false },
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: 3, colors: [baseColor] },
            xaxis: {
                categories: data.labels,
                axisBorder: { show: false },
                axisTicks: { show: false },
                tickAmount: Math.min(data.labels.length, 12),
                labels: {
                    rotate: 0,
                    hideOverlappingLabels: true,
                    style: { colors: labelColor, fontSize: "12px" }
                }
            },
            yaxis: {
                labels: {
                    style: { colors: labelColor, fontSize: "12px" },
                    formatter: (v: number) => "$" + (v >= 1000 ? (v / 1000).toFixed(1) + "K" : v.toFixed(0))
                }
            },
            tooltip: {
                style: { fontSize: "12px" },
                y: { formatter: (v: number) => moneyFormatter.format(v) }
            },
            colors: [baseColor],
            grid: { borderColor: borderColor, strokeDashArray: 4, yaxis: { lines: { show: true } } },
            markers: { strokeColor: baseColor, strokeWidth: 2 }
        }

        if (chartRef.current) {
            try { chartRef.current.destroy() } catch { }
        }
        chartRef.current = new Apex(ref.current, options)
        chartRef.current.render()

        return () => {
            if (chartRef.current) {
                try { chartRef.current.destroy() } catch { }
                chartRef.current = null
            }
        }
    }, [data])


    return (
        <div className="card">
            <div className="card-header border-0 pt-6">
                <div>
                    <h3 className="card-title fw-bold m-0">Wallet Usage</h3>
                    <div className="text-gray-500 fs-7 mt-1">
                        {moneyFormatter.format(data?.total ?? 0)} used{range?.label ? ` · ${range.label}` : ""}
                    </div>
                </div>
            </div>
            <div className="card-body pt-3">
                {
                    (fetching && !data) || !range ?
                        <div className="text-center py-15">
                            <Indicator />
                        </div>
                        :
                        <div ref={ref} style={{ minHeight: 300 }} />
                }
            </div>
        </div>
    )
}
